// The Fleet screen's pieces (P-004): a machine's card (status, vitals, slots, agent sessions,
// runners) and a runner's row with its restart. Everything shown comes from GET
// /api/workspaces/{ws}/fleet; nothing is worked out here but layout and words.
// Hook classes (no styles of their own; tests and the browser check select on them): host,
// session, runner (and machines-file, MachinesFile.tsx).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorMessage, type FleetCommand, type FleetHost, type FleetRunner, type FleetSession } from '../api/client';
import { fleetPath, recordPath } from '../lib/routes';
import { ago, duration } from '../lib/time';
import { Icon } from './Icon';
import { MachinesFileSection } from './MachinesFile';
import { Badge, Button, Card, ConfirmDialog, Meter, Sparkline, cn, mono, type BadgeTone } from './ui';

const SECTION_HEAD = 'flex items-baseline gap-1.5 px-4 pb-1.5 pt-3 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-ink-muted';
const ROW = 'grid min-w-0 items-center gap-x-4 gap-y-1 border-t border-hairline px-4 py-2.5';
const META = 'text-[11.5px] text-ink-muted';

export const KIND_LABEL: Record<FleetHost['kind'], string> = {
  orca: 'Orca host',
  amp: 'Amp host',
  agents: 'Agents',
  runners: 'Runners only',
  machine: 'Machine',
};

const OS_LABEL: Record<string, string> = { linux: 'Linux', macos: 'macOS' };

export function hostKind(h: FleetHost): string {
  return [KIND_LABEL[h.kind], h.os ? (OS_LABEL[h.os] ?? h.os) : null].filter(Boolean).join(' · ');
}

/** Online, last seen, or never reported. */
export function HostStatus({ host, now }: { host: FleetHost; now: number }) {
  if (host.online) return <Badge tone="success">Online</Badge>;
  if (!host.last_seen_at) return <Badge>Never reported</Badge>;
  return <Badge tone="warning">Last seen {ago(host.last_seen_at, now)}</Badge>;
}

/** One pip per slot; a coding session past the limit is a warning pip. */
export function Slots({ used, limit }: { used: number; limit: number }) {
  const over = used - limit;
  return (
    <span className="inline-flex flex-wrap items-center gap-2 text-[12px] text-ink-muted">
      <span className="inline-flex gap-[3px]" aria-hidden="true">
        {Array.from({ length: Math.max(used, limit) }, (_, i) => (
          <i
            key={i}
            className={cn(
              'block h-3 w-2 rounded-[2px] border',
              i < used ? (i >= limit ? 'border-warning bg-warning' : 'border-accent bg-accent') : 'border-hairline-strong',
            )}
          />
        ))}
      </span>
      <span>
        <b className="font-semibold text-ink-display">{used}</b> of {limit} slots
      </span>
      {over > 0 && <Badge tone="warning">{over} over its limit</Badge>}
    </span>
  );
}

const STATE: Record<FleetSession['state'], [BadgeTone, string]> = {
  working: ['signal', 'Working'],
  idle: ['muted', 'Idle'],
  done: ['success', 'Done'],
  unknown: ['muted', 'Unknown'],
};

function SessionState({ s, now }: { s: FleetSession; now: number }) {
  const [tone, text] = STATE[s.state];
  const label = s.kind === 'planning' && s.state === 'idle' ? 'Watching' : text;
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Badge tone={tone} spinner={s.state === 'working'}>
        {label}
      </Badge>
      {s.since && <span className="font-mono text-[11px] text-ink-muted">{duration(s.since, now)}</span>}
    </span>
  );
}

const TOOL: Record<FleetSession['tool'], string> = { orca: 'Orca', amp: 'Amp', process: 'process' };

function SessionRow({ s, ws, child, now }: { s: FleetSession; ws: number; child: boolean; now: number }) {
  const planning = s.kind === 'planning';
  const lastSegment = s.branch?.split('/').pop() ?? '';
  return (
    <div
      className={cn(
        'session',
        ROW,
        'grid-cols-[minmax(0,1.5fr)_minmax(0,1.5fr)_minmax(110px,auto)_minmax(70px,auto)] phone:grid-cols-[minmax(0,1fr)_auto]',
        child && 'pl-9 phone:pl-7',
      )}
      data-session={s.id}
      data-kind={s.kind}
    >
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-1.5">
          {child && <span className="text-ink-muted" aria-hidden="true">└</span>}
          {s.item ? (
            <Link
              to={recordPath(ws, s.item.repo_id, s.item.key)}
              className="flex-none rounded-[5px] border border-hairline-strong px-1.5 font-mono text-[11px] font-semibold text-ink-display hover:border-accent-display hover:text-accent-display hover:no-underline"
              aria-label={`${s.item.key}: ${s.item.title}`}
            >
              {s.item.key}
            </Link>
          ) : null}
          <span className="min-w-0 truncate font-medium text-ink-display">
            {planning ? `Planning · ${s.repo ?? 'session'}` : (s.item?.title ?? (lastSegment || s.repo || 'Session'))}
          </span>
        </div>
        <span className={cn(META, 'block truncate')}>
          {s.repo ?? 'unknown repo'} · {TOOL[s.tool]}
        </span>
      </div>
      <div className="min-w-0 phone:col-span-2 phone:row-start-2">
        {planning ? (
          <span className={META}>planning session</span>
        ) : (
          <>
            <span className={cn(mono, 'block truncate text-[11.5px] text-ink-body')} title={s.branch ?? undefined}>
              {s.branch ?? 'no branch'}
            </span>
            {s.pr !== null && <span className={META}>PR #{s.pr}</span>}
          </>
        )}
      </div>
      <div className="phone:col-start-2 phone:row-start-1 phone:justify-self-end">
        <SessionState s={s} now={now} />
      </div>
      <div className={cn(META, 'text-right phone:col-span-2 phone:text-left')}>
        <span className="sr-only">Last activity </span>
        {s.last_activity ? ago(s.last_activity, now) : '—'}
      </div>
    </div>
  );
}

/** Planning sessions, each with the coding sessions started from it beneath; then the rest. */
export function orderSessions(sessions: FleetSession[]): { s: FleetSession; child: boolean }[] {
  const ids = new Set(sessions.map((s) => s.id));
  const out: { s: FleetSession; child: boolean }[] = [];
  const placed = new Set<string>();
  const roots = sessions.filter((s) => !s.parent || !ids.has(s.parent));
  const visit = (s: FleetSession, child: boolean) => {
    if (placed.has(s.id)) return;
    placed.add(s.id);
    out.push({ s, child });
    for (const c of sessions.filter((x) => x.parent === s.id)) visit(c, true);
  };
  for (const r of roots.filter((s) => s.kind === 'planning')) visit(r, false);
  for (const r of roots.filter((s) => s.kind !== 'planning')) visit(r, false);
  for (const s of sessions) visit(s, false);
  return out;
}

const RUNNER_STATE: Record<FleetRunner['state'], [BadgeTone, string]> = {
  busy: ['signal', 'Busy'],
  idle: ['success', 'Idle'],
  offline: ['danger', 'Offline'],
  unknown: ['muted', 'Not on GitHub'],
};

export function RunnerBadge({ runner }: { runner: FleetRunner }) {
  const [tone, text] = RUNNER_STATE[runner.state];
  return (
    <Badge tone={tone} spinner={runner.state === 'busy'}>
      {text}
    </Badge>
  );
}

function CommandLine({ cmd, now, host }: { cmd: FleetCommand; now: number; host: string }) {
  const when = cmd.result_at ? ago(cmd.result_at, now) : '';
  const by = cmd.by ? ` by ${cmd.by}` : '';
  switch (cmd.state) {
    case 'queued':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="status">
          <Badge spinner>Queued</Badge>
          <span className="text-ink-muted">{host} picks this up on its next check-in, usually within a second.</span>
        </p>
      );
    case 'picked':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="status">
          <Badge tone="signal" spinner>
            {cmd.kind === 'update_reporter' ? 'Updating reporter' : cmd.kind === 'cleanup_worktree' ? 'Cleaning worktree' : 'Restarting'}
          </Badge>
          <span className="text-ink-muted">{host} is {cmd.kind === 'update_reporter' ? 'installing the verified reporter release' : cmd.kind === 'cleanup_worktree' ? 'checking the exact worktree cleanup request' : 'restarting the service'}.</span>
        </p>
      );
    case 'ok':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="status">
          <Badge tone="success">{cmd.kind === 'update_reporter' ? 'Reporter updated' : cmd.kind === 'cleanup_worktree' ? 'Worktree cleaned' : 'Restarted'}</Badge>
          <span className="text-ink-muted">
            {cmd.version ? `Version ${cmd.version} · ` : ''}{when}
            {by}
          </span>
        </p>
      );
    case 'failed':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="alert">
          <Badge tone="danger">{cmd.kind === 'update_reporter' ? 'Update failed' : cmd.kind === 'cleanup_worktree' ? 'Cleanup blocked' : 'Restart failed'}</Badge>
          <span className="text-danger-ink">{cmd.error}</span>
        </p>
      );
    case 'expired':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="alert">
          <Badge tone="warning">Not picked up</Badge>
          <span className="text-ink-muted">{host} did not collect the restart within 2 minutes.</span>
        </p>
      );
    case 'no_answer':
      return (
        <p className="m-0 flex flex-wrap items-center gap-2 text-[12px]" role="alert">
          <Badge tone="warning">No answer</Badge>
          <span className="text-ink-muted">{host} took the restart but has not said how it went.</span>
        </p>
      );
  }
}

function whyNoRestart(r: FleetRunner, online: boolean): string {
  if (!r.listed) return r.host ? 'Not on its machine’s list' : 'No reporter';
  if (!r.restart_enabled) return 'Restart switched off';
  if (!online) return 'Machine offline';
  return '';
}

export function RunnerRow({
  runner,
  ws,
  host,
  now,
  onChanged,
}: {
  runner: FleetRunner;
  ws: number;
  host: FleetHost | null;
  now: number;
  onChanged: () => void;
}) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gh = runner.github;
  const job = gh?.job;
  async function restart() {
    if (!host) return;
    setBusy(true);
    setError(null);
    const { data, error: err, response } = await api.POST(
      '/api/workspaces/{ws_id}/fleet/hosts/{host_id}/runners/{runner}/restart',
      { params: { path: { ws_id: ws, host_id: host.id, runner: runner.name } } },
    );
    setBusy(false);
    setConfirm(false);
    if (!data) setError(errorMessage(err, response.status));
    onChanged();
  }
  const detail = [
    job?.repo ? `repo ${job.repo}` : null,
    job?.branch ? job.branch.split('/').pop() : null,
    job?.started_at ? `started ${ago(job.started_at, now)}` : null,
    runner.unit_state && runner.unit_state !== 'failed' ? `service ${runner.unit_state}` : null,
  ].filter(Boolean);
  return (
    <div className={cn('runner', ROW, 'grid-cols-[minmax(0,1fr)_minmax(0,2fr)_auto] phone:grid-cols-[minmax(0,1fr)_auto]')} data-runner={runner.name}>
      <div className="min-w-0">
        <b className={cn(mono, 'block truncate font-semibold text-ink-display')}>{runner.name}</b>
        <span className={cn(META, 'block truncate')}>{gh ? gh.source : 'not listed on GitHub'}</span>
        {gh && gh.labels.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1" aria-label="Runner labels">
            {gh.labels.map((label) => <Badge key={label} tone="muted" className="[overflow-wrap:anywhere]">{label}</Badge>)}
          </div>
        )}
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 phone:col-span-2 phone:row-start-2">
        <RunnerBadge runner={runner} />
        {job && (
          <span className="font-medium text-ink-display">
            {[job.workflow, job.job].filter(Boolean).join(' / ')}
          </span>
        )}
        {detail.length > 0 && <span className={META}>{detail.join(' · ')}</span>}
        {runner.unit_state === 'failed' && <Badge tone="danger">service failed</Badge>}
      </div>
      <div className="justify-self-end phone:col-start-2 phone:row-start-1">
        {runner.can_restart && host ? (
          <Button size="sm" onClick={() => setConfirm(true)} disabled={busy}>
            <Icon name="refresh" small />
            Restart
          </Button>
        ) : (
          <span className={META}>{whyNoRestart(runner, host?.online ?? false)}</span>
        )}
      </div>
      {(runner.last_command || error) && (
        <div className="col-span-full">
          {error ? (
            <p className="m-0 text-[12px] font-medium text-danger-ink" role="alert">
              {error}
            </p>
          ) : (
            runner.last_command && host && <CommandLine cmd={runner.last_command} now={now} host={host.name} />
          )}
        </div>
      )}
      {confirm && host && (
        <ConfirmDialog
          title={`Restart ${runner.name}?`}
          confirmText={runner.name}
          action="Restart runner"
          busy={busy}
          onCancel={() => setConfirm(false)}
          onConfirm={() => void restart()}
        >
          <p className="m-0">
            {runner.state === 'busy'
              ? 'It is running a job right now, and that job will fail.'
              : 'It is not running a job, so nothing should be interrupted.'}{' '}
            {host?.name} restarts its service <span className={mono}>{runner.unit}</span>.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/** GitHub runners without a reporter match, kept visible outside machine cards. */
export function UnattachedRunners({ runners, ws, now }: { runners: FleetRunner[]; ws: number; now: number }) {
  if (runners.length === 0) return null;
  return (
    <Card as="section" padding="none" aria-label="Not on a machine">
      <h2 className={cn(SECTION_HEAD, 'm-0')}>Not on a machine <span className="font-normal normal-case tracking-normal">· {runners.length}</span></h2>
      {runners.map((runner) => (
        <RunnerRow key={runner.name} runner={runner} ws={ws} host={null} now={now} onChanged={() => {}} />
      ))}
    </Card>
  );
}

export function HostCard({ host, ws, now, onChanged }: { host: FleetHost; ws: number; now: number; onChanged: () => void }) {
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const coding = host.sessions.filter((s) => s.kind === 'coding').length;
  const planning = host.sessions.filter((s) => s.kind === 'planning').length;
  const busyPaths = new Set(host.sessions.map((s) => s.path));
  const idle = host.worktrees.filter((w) => !busyPaths.has(w.path));
  const idleRepos = [...new Set(idle.map((w) => w.repo).filter(Boolean))];
  const unexpected = Object.entries(host.sources ?? {}).filter(([, v]) => v === 'unexpected').map(([k]) => k);
  const sub = [
    hostKind(host),
    host.load1 !== null ? `load ${host.load1}` : null,
    host.reporter ? `reporter ${host.reporter}` : null,
    host.last_seen_at ? `reported ${ago(host.last_seen_at, now)}` : 'waiting for its first report',
  ].filter(Boolean);
  const update = async () => {
    setUpdating(true);
    setUpdateError(null);
    const { error } = await api.POST('/api/workspaces/{ws_id}/fleet/hosts/{host_id}/update', {
      params: { path: { ws_id: ws, host_id: host.id } },
    });
    setUpdating(false);
    if (error) setUpdateError(errorMessage(error));
    else onChanged();
  };
  return (
    <Card as="article" padding="none" className="host" data-host={host.name} aria-labelledby={`host-${host.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4 px-4 pb-3 pt-3.5">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-ink-muted">
              <Icon name="server" />
            </span>
            <h2 id={`host-${host.id}`} className="m-0 font-mono text-[15px] font-semibold">
              {host.name}
            </h2>
            <HostStatus host={host} now={now} />
            {host.slot_limit !== null && <Slots used={host.slots_used} limit={host.slot_limit} />}
            {host.update_available && (
              host.online && host.update_configured ? (
                <Button size="sm" onClick={() => void update()} disabled={updating || host.update_command?.state === 'queued' || host.update_command?.state === 'picked'}>
                  <Icon name="refresh" small />
                  {updating ? 'Queueing update' : 'Update reporter'}
                </Button>
              ) : <Badge tone="warning">Update available · {host.online ? 'release not configured' : 'host offline'}</Badge>
            )}
          </div>
          <p className={cn(META, 'm-0 mt-1')}>{sub.join(' · ')}</p>
          {host.update_command && <div className="mt-1"><CommandLine cmd={host.update_command} now={now} host={host.name} /></div>}
          {updateError && <p className="m-0 mt-1 text-[12px] text-danger-ink" role="alert">{updateError}</p>}
          {unexpected.length > 0 && (
            <p className="m-0 mt-1 text-[11.5px] text-warning-ink">
              {unexpected.join(', ')} answered in a shape the reporter does not know; its sessions are left out.
            </p>
          )}
        </div>
        {host.cpu_pct !== null && (
          <div className="grid w-[min(420px,100%)] grid-cols-[auto_1fr_1fr_1fr] items-end gap-4 phone:grid-cols-3">
            {host.cpu_24h.length > 1 ? (
              <span className="grid justify-items-start gap-0.5 text-accent phone:col-span-3">
                <Sparkline values={host.cpu_24h} width={120} height={28} label={`CPU over the last 24 hours, now ${host.cpu_pct}%`} />
                <span className="text-[10.5px] text-ink-muted">CPU, last 24 h</span>
              </span>
            ) : (
              <span className="phone:hidden" />
            )}
            <Meter label="CPU" value={host.cpu_pct} max={100} warnAt={85} valueText={`${Math.round(host.cpu_pct)}%`} />
            {host.mem_pct !== null && (
              <Meter label="Memory" value={host.mem_pct} max={100} warnAt={85} valueText={`${Math.round(host.mem_pct)}%`} />
            )}
            {host.disk_pct !== null && (
              <Meter label="Disk" value={host.disk_pct} max={100} warnAt={85} valueText={`${Math.round(host.disk_pct)}%`} />
            )}
          </div>
        )}
        {(host.disks ?? []).length > 0 && (
          <section className="mt-3 grid max-w-[640px] gap-2" aria-label={`Storage volumes on ${host.name}`}>
            {(host.disks ?? []).map((disk) => (
              <div key={`${disk.mount}:${disk.name}`} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1">
                <span className="truncate text-[11.5px] font-medium text-ink-display" title={`${disk.name} · ${disk.mount}`}>
                  {disk.name} <span className="font-mono font-normal text-ink-muted">{disk.mount}</span>
                </span>
                <span className="font-mono text-[11.5px] text-ink-muted">
                  {`${Math.round(disk.used_pct)}% · ${formatBytes(disk.used_bytes)} / ${formatBytes(disk.total_bytes)}`}
                </span>
                <div className="col-span-2 h-1 overflow-hidden rounded-full bg-surface-raised" aria-label={`${disk.name} ${Math.round(disk.used_pct)}% used`}>
                  <div className="h-full rounded-full bg-accent" style={{ width: `${disk.used_pct}%` }} />
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
      {host.sessions.length > 0 && (
        <section aria-label={`Agent sessions on ${host.name}`}>
          <h3 className={SECTION_HEAD}>
            Agent sessions{' '}
            <span className="font-normal normal-case tracking-normal">
              · {coding} coding, {planning} planning
            </span>
          </h3>
          {orderSessions(host.sessions).map(({ s, child }) => (
            <SessionRow key={s.id} s={s} ws={ws} child={child} now={now} />
          ))}
        </section>
      )}
      {idle.length > 0 && (
        <p className={cn(META, 'm-0 border-t border-hairline px-4 py-2.5')}>
          {idle.length} {idle.length === 1 ? 'checkout' : 'checkouts'} with no agent{idleRepos.length ? `: ${idleRepos.join(', ')}` : ''}
        </p>
      )}
      {host.runners.length > 0 && (
        <section aria-label={`Runners on ${host.name}`}>
          <h3 className={SECTION_HEAD}>
            Runners <span className="font-normal normal-case tracking-normal">· {host.runners.length}</span>
          </h3>
          {host.runners.map((r) => (
            <RunnerRow key={r.name} runner={r} ws={ws} host={host} now={now} onChanged={onChanged} />
          ))}
        </section>
      )}
      {host.sessions.length === 0 && host.runners.length === 0 && idle.length === 0 && (
        <p className={cn(META, 'm-0 border-t border-hairline px-4 py-2.5')}>
          {host.last_seen_at ? 'No agents and no runners on this machine.' : 'Install the reporter on this machine to see it here.'}
        </p>
      )}
      <MachinesFileSection host={host} ws={ws} now={now} onChanged={onChanged} />
    </Card>
  );
}

function formatBytes(value: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  const shown = size >= 10 || unit === 0 ? Math.round(size) : Number(size.toFixed(1));
  return `${shown} ${units[unit]}`;
}

/** "agent working · orca-vps" on a board card, linking to the fleet. */
export function AgentChip({ ws, host, state }: { ws: number; host: string; state: string }) {
  return (
    <Link
      to={fleetPath(ws)}
      className="relative z-[1] inline-flex items-center gap-1.5 text-[11.5px] text-signal-ink hover:no-underline"
      aria-label={`Agent ${state === 'working' ? 'working' : 'idle'} on ${host}: open the fleet`}
    >
      {state === 'working' ? <span className="inline-block size-2 flex-none animate-pulse rounded-full bg-signal" aria-hidden="true" /> : null}
      agent {state === 'working' ? 'working' : 'idle'} · {host}
    </Link>
  );
}
